/**
 * Фигура героя в полный рост для экрана «Герой».
 *
 * Назначение файла: показать выбранный облик с ореолом и парением, а если файл
 * картинки не докачался — оставить экран целым: вместо фигуры встаёт сота с
 * инициалом ника. Облик — чистая косметика, статы сюда не приходят.
 *
 * Импорты: список обликов из `game/appearance`, подписи из каталога переводов.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { heroAppearance, type HeroAppearanceId } from "../../game/appearance.js";

export interface HeroFigureProps {
  readonly appearanceId: HeroAppearanceId;
  readonly nickname: string;
  /** Уровень в бейдже у плеча фигуры. */
  readonly level: number;
}

/** Первая буква ника для запасного силуэта. Ник — данные игрока, не перевод. */
function initial(nickname: string): string {
  return [...nickname.trim()].slice(0, 1).join("").toUpperCase();
}

export function HeroFigure({
  appearanceId,
  nickname,
  level,
}: HeroFigureProps): React.JSX.Element {
  const { t } = useTranslation("hero");
  const [broken, setBroken] = useState(false);
  const appearance = heroAppearance(appearanceId);

  return (
    <div className="hero-figure">
      <span className="hero-figure__halo anim-breathe" aria-hidden="true" />
      <span className="hero-figure__floor" aria-hidden="true" />

      {broken ? (
        <span className="hero-figure__fallback" aria-hidden="true">
          {initial(nickname)}
        </span>
      ) : (
        <img
          className="hero-figure__art anim-float"
          src={appearance.figure}
          alt={t(`appearance.${appearance.id}`)}
          width={560}
          height={720}
          decoding="async"
          onError={() => setBroken(true)}
        />
      )}

      <span className="hero-figure__motes" aria-hidden="true">
        <i style={{ "--i": 0 } as React.CSSProperties} />
        <i style={{ "--i": 1 } as React.CSSProperties} />
        <i style={{ "--i": 2 } as React.CSSProperties} />
      </span>

      {/*
        В соте только число: слово «уровень» уже стоит плашкой рядом с ником, и
        второй раз в медали оно не влезало в шестиугольник без переноса.
      */}
      <span className="hero-figure__badge" aria-hidden="true">
        <b className="hero-figure__badge-value">{level}</b>
      </span>
    </div>
  );
}
